// SelectedPartCard — WHAT THE SELECTED PART SAYS IT IS, beside what the DB believes.
//
// Alex's two-area vision for the Onshape right panel (2026-08-21): "Top, what we
// have right now… This one gets information from the DB based on me pasting the
// link. Bottom, parts that will get information from the part number or
// subassembly id, one that will double check on the information without spending
// one call."
//
// So this card is the CHECKING half. The cards above it answer "what did I link
// this element to"; this one answers "what does the model actually carry", and the
// two are deliberately kept apart so they can disagree in front of you. Nothing
// compared them before, which is how an id landed in the wrong field three times.
//
// ⚠ THE PART NUMBER IS THE ONLY ROUTE, AND THAT IS SETTLED, NOT ASSUMED.
// Alex's 2026-08-21 screenshot ran the experiment the panel was built for:
// `{$db_id}` came back as the DEAD LITERAL while every other token substituted —
// Onshape does not substitute custom properties into an Action URL, so a part's
// db_id property can never reach us this way. The part number can, and did
// (`R2RSE` → `fam:46`, "via part number"). That is why his `XXXXXXX [prod:900]`
// convention is the mechanism rather than a nicety, and why the server's search is
// unanchored: the id rides INSIDE a human-readable number, it does not replace it.
//
// ZERO ONSHAPE CALLS. One GET to our own /api/onshape/part-identity, which is DB
// reads plus the metadata cache we already paid for. If a future version of this
// card needs a live read, it must NOT join this path — the whole value of the
// bottom area is that checking is free, so it can be done constantly.
import { useState, useEffect } from 'react';
import Card from './Card.jsx';
import Notice from './Notice.jsx';
import DbIdBadge from './DbIdBadge.jsx';
import { dbHrefFor } from '../nav-link.js';
import { LINK } from '../icons.js';
import { NAVY } from '../theme-tokens.js';
// moneyUnit, NOT money: a catalog line price is a UNIT price, and format.js says
// why in its own words - "rounding a unit price changes every quantity computed
// from it". $55.60 must not print as $56 in the card that exists to be checked
// against the model.
import { moneyUnit } from '../format.js';
import { actionUrlProblems, liveVerdict } from '../onshape-panel-diagnosis.js';

const LINK_STYLE = { color: NAVY, fontWeight: 600, textDecoration: 'underline' };
const K = { fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' };
const V = { fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' };

// A family/product hit → the deep link into Core, via the one builder that knows
// `node` from `prod` (nav-link.js — building it by hand is what silently broke
// every product link before 2026-08-21).
function hrefFor(hit) {
  if (!hit) return null;
  if (hit.kind === 'product') return dbHrefFor('products', `c3-${hit.category_id}|${hit.id}`);
  const famId = hit.kind === 'family' ? hit.id : hit.family_id;
  return dbHrefFor('catalog', `c3-${hit.category_id}|fam-${famId}`);
}

// tokens: what Onshape actually substituted into OUR url. `raw` distinguishes the
// three states the card must tell apart — see naReason below.
// live: useOnshapeSelection()'s result (onshape-selection.js), or null. The URL
// tokens are frozen at iframe load; once a readable SELECTION arrives it WINS,
// because it is newer — and a deselect or a face clears the part rather than
// leaving the load-time one on screen as if it were still selected.
export default function SelectedPartCard({ tokens: urlTokens, live = null, elementDbId = null, storageKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // A SELECTION naming the SAME part the URL already carries adds nothing, and
  // replacing the URL tokens would throw away the part number — the db_id's main
  // route. So it only wins when it says something the URL does not.
  const liveSel = live?.selection || null;
  const sel = liveSel?.kind === 'body' && urlTokens.partId && liveSel.partId === urlTokens.partId ? null : liveSel;
  const tokens = !sel ? urlTokens
    : { ...urlTokens, partNumber: '', partId: sel.kind === 'body' ? sel.partId : '',
        occurrencePath: sel.kind === 'occurrence' ? sel.path.join(',') : '' };
  const source = sel
    ? `from your selection · ${live.at ? live.at.toLocaleTimeString() : ''}`
    : 'from the panel URL — fixed when the panel loaded';

  const hasPart = !!tokens.partNumber || !!tokens.partId || !!tokens.occurrencePath;

  useEffect(() => {
    if (!hasPart) { setData(null); return; }
    const q = new URLSearchParams();
    for (const k of ['partNumber', 'partId', 'occurrencePath', 'documentId', 'elementId', 'configuration']) {
      if (tokens[k]) q.set(k, tokens[k]);
    }
    let dead = false;
    fetch(`/api/onshape/part-identity?${q.toString()}`)
      .then(r => r.json().then(b => { if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`); return b; }))
      .then(b => { if (!dead) { setData(b); setError(null); } })
      .catch(e => { if (!dead) setError(e.message); });
    return () => { dead = true; };
  }, [hasPart, tokens.partNumber, tokens.partId, tokens.occurrencePath, tokens.documentId, tokens.elementId, tokens.configuration]);

  // THREE ABSENT-STATES, AND THEY ARE NOT THE SAME PROBLEM. Collapsing them into
  // one "nothing selected" would send Alex hunting in Onshape for a fault that is
  // actually in the Action URL — the exact wrong-place-to-look this panel exists
  // to stop. `rawPartNumber` is the discriminator: a MISSING param means the
  // registration never sends it; the un-substituted LITERAL means it sends it and
  // Onshape had nothing to put there.
  //
  // With the LIVE route (ON-42) the URL no longer decides alone: once Onshape has
  // said what is selected, the reason names THAT — a face, several items, an
  // assembly instance — because "no part selected" would be false for each.
  const listening = live?.status === 'listening';
  const naReason = sel?.kind === 'entity'
    ? `A ${sel.entityType.toLowerCase()} is selected — select the whole part to look it up.`
    : sel?.kind === 'multiple'
      ? `${sel.count} items selected — select exactly one part.`
      : sel?.kind === 'occurrence'
        ? 'An assembly instance is selected — the live lookup covers part-studio parts only for now. For assemblies, use the Selected instance panel.'
        : sel?.kind === 'none'
          ? 'No part selected — pick one whole part in Onshape and this fills in, at no API cost.'
          : listening && !live.count
            ? 'No part selected — pick one whole part in Onshape. Listening for Onshape’s selection; none received yet. If a click changes nothing, the selection is not reaching this panel.'
            : tokens.rawPartNumber == null
              ? 'This panel’s Action URL does not send the part number yet — add the part tokens to the registration in Onshape.'
              : 'No part selected — pick one part in Onshape and this fills in, at no API cost.';

  const hit = data?.hit || null;
  const selLine = hit?.kind === 'family' && data?.selectedLineId != null
    ? (hit.lines || []).find(l => l.id === data.selectedLineId) : null;

  // ALWAYS OPENABLE since the Onshape context card was retired (2026-10-08). A
  // #NA card cannot be opened, so its reason lived in a tooltip nobody found —
  // which is why the live-selection status had been parked in that other card.
  // Now the reason is the body: with no part, the card says why, and adds the
  // two diagnostics that used to need a card of their own.
  const urlProblems = actionUrlProblems();
  const verdict = liveVerdict(live);

  return (
    <Card storageKey={storageKey} title="Selected part" count={hasPart ? undefined : 'no part'}>
      {error && <Notice kind="error">{error}</Notice>}

      {!hasPart && (
        <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
          <div>{naReason}</div>
          {verdict && <div style={{ ...K, marginTop: 4 }}>{verdict}</div>}
        </div>
      )}

      {urlProblems.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
          {urlProblems.map((p, i) => <li key={i} style={{ marginBottom: 2 }}>{p}</li>)}
        </ul>
      )}

      {data && (
        <>
          <div style={{ ...K, marginBottom: 4 }}>{source}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 6 }}>
            <span style={K}>Part number</span>
            <span style={{ ...V, fontFamily: 'var(--ff-mono)', wordBreak: 'break-all' }}>
              {data.partNumber || '—'}
            </span>
          </div>

          {/* ⚠ THE REGISTRATION IS WRONG, AND IT COSTS THE SIZE ANSWER. Measured
              2026-08-21: `Sch=S10;Size=NPS03;Type=A` resolves to a real line; the
              same string with `&db_id={$db_id}` appended resolves to nothing. So
              this is not a tidiness note — without the fix the card cannot say
              which size the part is. */}
          {data.configBled && (
            <Notice kind="warning" title={data.configRaw}>
              A parameter after <code>{'{$configuration}'}</code> bled into the configuration value.
              It is cut here so the size still resolves, but fix the Action URL:
              keep <code>{'{$configuration}'}</code> LAST.
            </Notice>
          )}

          {data.partName && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 6 }}>
              <span style={K}>Name</span>
              <span style={V}>{data.partName}</span>
            </div>
          )}

          {/* ASSEMBLY EVIDENCE (ON-42). What the assembly's last read says about
              this instance, and HOW it was matched — or, when nothing matched,
              what the read's instance fields actually look like. Shown raw on
              purpose: the shape of Onshape's BOM occurrence data was unmeasured
              when this was written, and this is where it gets measured. */}
          {data.assembly && (
            <div style={{ ...K, marginBottom: 6 }}>
              Assembly read: {data.assembly.state === 'ok'
                ? `row found (${data.assembly.match === 'path' ? 'exact instance path' : 'by instance id only'})`
                : data.assembly.state === 'no-cache' ? 'none saved yet'
                : data.assembly.state === 'no-part' ? 'saved, but no row names this instance'
                : data.assembly.state || '—'}
              {data.assembly.read && ` · ${data.assembly.read.rows} rows · read ${String(data.assembly.read.at).slice(0, 16).replace('T', ' ')} UTC`}
              {data.assembly.evidence && (
                <pre style={{ margin: '4px 0 0', padding: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-small)', color: 'var(--th-text-body)', border: '1px solid var(--th-border-subtle)' }}>
                  {data.assembly.evidence}
                </pre>
              )}
            </div>
          )}

          {!data.dbId && data.cache === 'no-cache' ? (
            /* A LIVE selection carries only a partId, so the part's number, name
               and db_id can only come from this element's last paid read. With no
               read at all, "carries no db_id" would be a false accusation. */
            <Notice kind="warning">
              This {data.assembly ? 'assembly' : 'element'} has not been read yet, so the selected {data.assembly ? 'instance' : 'part'} cannot be looked up.
              Press <b>Sync from Onshape</b> once (or <b>Read this element</b> in Tag Audit
              when the element is not a product) — after that every selection here resolves
              at no cost.
            </Notice>
          ) : !data.dbId && data.cache === 'no-part' ? (
            <Notice kind="warning">
              {data.assembly
                ? <>The last read of this assembly has no row for this instance — its instance fields are shown above. Sync again (or re-read it in Tag Audit) if the instance is newer than that read.</>
                : <>This part is not in the last read of this element — it is newer than that read. Sync again (or re-read it in Tag Audit) to include it.</>}
            </Notice>
          ) : !data.dbId && data.misplacedId ? (
            <Notice kind="warning">
              The part number carries <b>{data.misplacedId}</b>, but the part’s <code>db_id</code> is
              empty — the id landed one field over. Move it into <code>db_id</code>.
            </Notice>
          ) : !data.dbId ? (
            /* THE ACTIONABLE STATE. No id in the part number is the normal state
               for an untagged part — so the card's job here is not to complain but
               to hand over the id to paste. */
            <Notice kind="warning">
              This part carries no <code>db_id</code> in its part number.
              {elementDbId
                ? <> The element above is <b>{elementDbId}</b> — if this part is that item, put the id in its part number.</>
                : <> Link the element above first, then its id can go on the part.</>}
            </Notice>
          ) : !hit ? (
            <Notice kind="error">
              The part number carries <b>{data.dbId}</b>, but no such row exists in Core — the id is stale or mistyped.
            </Notice>
          ) : (
            <>
              <div style={{ marginBottom: 4 }}>
                <span style={{ fontFamily: 'var(--ff-mono)', fontWeight: 700, fontSize: 'var(--fs-title)', color: 'var(--th-text-body)' }}>
                  {hit.code}
                </span>
                <span style={{ ...V, marginLeft: 6 }}>{hit.label}</span>
              </div>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                <DbIdBadge dbId={data.dbId} variant="row" />
                <span style={{ ...K, fontSize: 'var(--fs-small)' }}>via {data.via}</span>
              </div>

              {/* Which SIZE — the configuration's own answer, resolved by the same
                  diagnoser the sync uses, so the card and the sync can never
                  disagree about what this part is. */}
              {hit.kind === 'family' && (
                selLine ? (
                  <div style={{ ...V, marginTop: 4 }}>
                    Size <b>{selLine.look || selLine.size_id}</b> — {moneyUnit(selLine.price)}
                    {selLine.hrs != null ? ` / ${selLine.hrs} h` : ''}
                  </div>
                ) : (
                  <div style={{ ...K, marginTop: 4 }}>
                    {data.configuration
                      ? 'The configuration does not match any size in this family.'
                      : 'No configuration sent — size not resolved.'}
                  </div>
                )
              )}

              {/* A config that resolves into a DIFFERENT family than the db_id
                  names is a real finding (Angle=A90 against a 45° family), not a
                  rendering problem — so it is said, not swallowed. */}
              {data.resolvedElsewhere && (
                <Notice kind="warning">
                  The configuration resolves into a different family than the id names —
                  the part may carry the wrong <code>db_id</code>.
                </Notice>
              )}

              <div style={{ marginTop: 6 }}>
                <a href={hrefFor(hit)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
                  Open {hit.kind === 'product' ? 'the product' : 'the family'} in Core {LINK}
                </a>
              </div>
            </>
          )}

          {/* Element vs part, shown side by side. Deliberately NOT flagged as an
              error when they differ: inside an ASSEMBLY the element is the product
              and its parts are other families, so disagreement is the normal case
              there. Stating both and letting Alex read them is honest; a warning
              that cries wolf on every assembly would be trained away in a day. */}
          {elementDbId && data.dbId && elementDbId !== data.dbId && (
            <div style={{ ...K, marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--th-border-subtle)' }}>
              Element is <b>{elementDbId}</b> · this part is <b>{data.dbId}</b>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
